import React, { useMemo } from "react";
import { getSurface } from "./surfaces";
import { getEffect } from "./effects";
import "./RealisticStage.css";

/**
 * Test feature: dresses a card preview up as a photograph of the physical
 * hardcopy sitting on a real surface - slight tilt, a drop shadow tinted to
 * that surface, a shallow-depth-of-field backdrop, a moving glare hotspot,
 * and a photographic grain overlay. Two independent choices layer on top of
 * that base: `surfaceKey` (see surfaces.js) picks the backdrop material -
 * three real photographs plus several CSS-only recipes (marble, leather,
 * linen, studio grey) - and `effectKey` (see effects.js) picks a "look"
 * preset that retunes the grain/ageing/lighting intensity as one bundle
 * (Natural, Vintage, Camera flash, Low light, Studio clean) rather than a
 * single fixed aesthetic.
 *
 * `card` and `veil` are rendered inside the same `.ac-card-shield` box the
 * plain preview already uses (App.css), so the screenshot-blur behaviour is
 * untouched and the grain/sheen layers clip to the card's rounded corners
 * for free.
 *
 * This component is the preview only. Exports do not screenshot it - the
 * look here is built from `mix-blend-mode` and CSS `filter`, neither of which
 * html2canvas reproduces - so the same layers are composited on a canvas for
 * downloads instead; see utils/realisticScene.js, which mirrors this file
 * layer for layer and must be kept in step with it.
 */
export default function RealisticStage({
  card,
  veil,
  tilt = -2,
  surfaceKey = "default",
  effectKey = "natural",
  pixelBlur = false,
  damageLevel = "none",
  bgBlur = 40,
  cardRadius,
  groundShadow = false,
}) {
  const surface = getSurface(surfaceKey);
  const effect = getEffect(effectKey);

  // A little per-mount jitter so front and back - and every re-roll - don't
  // read as the exact same template stamped twice. Stable for the life of
  // this component instance; it only reshuffles when the card it belongs to
  // actually remounts (new side, new surface).
  const jitter = useMemo(
    () => ({
      tilt: (Math.random() - 0.5) * 1.4,
      glareX: 18 + Math.random() * 34,
      glareY: 10 + Math.random() * 28,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [surfaceKey]
  );

  const surfaceStyle = surface.image ? undefined : { background: surface.css };

  const cardStyle = {
    ...effect.vars,
    // Handed down so .ac-card-shield clips the grain and sheen to the same
    // corner the card is drawn with (see App.css).
    ...(cardRadius != null ? { "--card-radius": `${cardRadius}px` } : null),
    "--tilt": `${tilt + jitter.tilt}deg`,
    "--shadow-tint": surface.shadow,
    "--glare-x": `${jitter.glareX}%`,
    "--glare-y": `${jitter.glareY}%`,
    "--bg-blur": `${(bgBlur / 100) * 3.2}px`,
  };

  return (
    <div className={`rp-surface${surface.image ? " has-photo" : ""}`} style={surfaceStyle}>
      {surface.image && (
        <img className="rp-bg-photo" src={surface.image} alt="" aria-hidden="true" />
      )}
      <div
        className={
          "rp-card-wrap" +
          (pixelBlur ? " blur-pixel" : "") +
          (groundShadow ? " has-ground" : "") +
          (damageLevel !== "none" ? ` damage-${damageLevel}` : "")
        }
        style={cardStyle}
      >
        <div className="ac-card-shield">
          {card}
          <div className="ac-card-watermark" aria-hidden="true" />
          {veil}
          <div className="rp-grain" aria-hidden="true" />
          <div className="rp-sheen" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}
